import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  Loader2, Upload, Trash2, X, Save, CheckCircle2, ImageIcon, Plus,
  ArrowUp, ArrowDown, RefreshCw,
} from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import { IMAGES, EVENTS_PHOTOS } from "@/config/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SLOTS = [
  { key: "hero", label: "Home — Hero" },
  { key: "community", label: "Home — Families & Youth band" },
  { key: "repairs", label: "Repairs page — Hero" },
  { key: "workshop", label: "Services — No-Ego Service" },
  { key: "fitting", label: "Fitting page — Hero" },
  { key: "noteworthy", label: "Services — Make It Yours" },
];

const captionCls =
  "w-full rounded-xl border-2 border-stone-200 bg-stone-50 px-3 py-2 text-sm text-leaf-900 focus:border-leaf-500 focus:bg-white focus:outline-none focus:ring-2 focus:ring-leaf-200 transition";

function PickerModal({ photos, onPick, onClose }) {
  return (
    <div
      data-testid="photo-picker-modal"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-leaf-900/50 backdrop-blur-sm" />
      <div
        className="relative max-h-[80vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="font-display text-2xl font-bold text-leaf-900">Choose a photo</h3>
          <button
            data-testid="photo-picker-close"
            onClick={onClose}
            aria-label="Close picker"
            className="rounded-full bg-leaf-100 p-2 active:scale-95"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {photos.length === 0 ? (
          <p className="mt-6 text-leaf-900/60">
            No photos in the library yet — upload some from the Photo Library section first.
          </p>
        ) : (
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
            {photos.map((p) => (
              <button
                key={p.id}
                data-testid={`picker-photo-${p.id}`}
                onClick={() => onPick(p.url)}
                className="group overflow-hidden rounded-2xl border-2 border-leaf-100 transition-all hover:-translate-y-0.5 hover:border-leaf-400 hover:shadow-lg"
              >
                <img src={p.url} alt={p.original_filename} className="h-28 w-full object-cover" loading="lazy" />
                <p className="truncate px-2 py-1.5 text-left text-[11px] font-semibold text-leaf-900/70">
                  {p.original_filename}
                </p>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function LibrarySection({ photos, loading, error, uploading, uploadErr, fileRef, onUpload, onRefresh, onDelete }) {
  return (
    <section className="rounded-3xl border-2 border-leaf-100 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-leaf-900">Photo library</h2>
          <p className="text-sm text-leaf-900/60">
            Upload once, reuse anywhere. iPhone HEIC photos are converted & resized automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="photos-refresh"
            onClick={onRefresh}
            aria-label="Refresh library"
            className="rounded-full border-2 border-leaf-200 p-2.5 text-leaf-700 hover:bg-leaf-50"
          >
            <RefreshCw className="h-4 w-4" />
          </button>
          <label className="inline-flex cursor-pointer items-center gap-2 rounded-full bg-leaf-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? "Uploading…" : "Upload photos"}
            <input
              ref={fileRef}
              data-testid="photos-upload-input"
              type="file"
              accept="image/*,.heic,.heif"
              multiple
              onChange={onUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
      </div>
      {uploadErr && (
        <p data-testid="photos-upload-error" className="mt-3 rounded-xl bg-clay-500/10 px-4 py-2.5 text-sm text-clay-600">
          {uploadErr}
        </p>
      )}
      {error && (
        <p data-testid="photos-library-error" className="mt-3 rounded-xl bg-clay-500/10 px-4 py-2.5 text-sm text-clay-600">
          {error}
        </p>
      )}
      {loading ? (
        <div className="flex justify-center py-10 text-leaf-700">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      ) : photos.length === 0 ? (
        <div className="mt-5 flex flex-col items-center gap-2 rounded-2xl border-2 border-dashed border-leaf-200 bg-leaf-50/40 py-10 text-leaf-900/60">
          <ImageIcon className="h-8 w-8" />
          <p className="font-semibold">No photos yet — upload your first one!</p>
        </div>
      ) : (
        <div data-testid="photos-library-grid" className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-5">
          {photos.map((p) => (
            <div key={p.id} data-testid={`photo-card-${p.id}`} className="group relative overflow-hidden rounded-2xl border-2 border-leaf-100">
              <img src={p.url} alt={p.original_filename} className="h-28 w-full object-cover" loading="lazy" />
              <button
                data-testid={`photo-delete-${p.id}`}
                onClick={() => onDelete(p.id)}
                aria-label="Delete photo"
                className="absolute right-1.5 top-1.5 rounded-full bg-white/90 p-1.5 text-clay-500 opacity-0 shadow transition-opacity hover:bg-white group-hover:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
              <p className="truncate px-2 py-1.5 text-[11px] font-semibold text-leaf-900/70">
                {p.original_filename}
              </p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SlotsSection({ images, onChangeSlot }) {
  return (
    <section className="rounded-3xl border-2 border-leaf-100 bg-white p-6">
      <h2 className="font-display text-2xl font-bold text-leaf-900">Page photos</h2>
      <p className="text-sm text-leaf-900/60">Which photo shows where on the site.</p>
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {SLOTS.map((s) => (
          <div key={s.key} data-testid={`slot-${s.key}`} className="overflow-hidden rounded-2xl border-2 border-leaf-100">
            <img src={images[s.key]} alt={s.label} className="h-36 w-full object-cover" loading="lazy" />
            <div className="flex items-center justify-between gap-2 p-3">
              <p className="text-sm font-bold text-leaf-900">{s.label}</p>
              <button
                data-testid={`slot-change-${s.key}`}
                onClick={() => onChangeSlot(s.key)}
                className="shrink-0 rounded-full bg-leaf-100 px-4 py-1.5 text-xs font-bold text-leaf-700 hover:bg-leaf-200"
              >
                Change
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function EventsSection({ events, onCaption, onMove, onRemove, onAdd }) {
  return (
    <section className="rounded-3xl border-2 border-leaf-100 bg-white p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold text-leaf-900">Events carousel</h2>
          <p className="text-sm text-leaf-900/60">Photos, captions, and order on the Events page.</p>
        </div>
        <button
          data-testid="events-add-photo"
          onClick={onAdd}
          className="inline-flex items-center gap-2 rounded-full bg-leaf-600 px-5 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700"
        >
          <Plus className="h-4 w-4" /> Add photo
        </button>
      </div>
      <div className="mt-5 space-y-3">
        {events.map((p, i) => (
          <div key={`${p.src}-${i}`} data-testid={`events-photo-row-${i}`} className="flex items-center gap-3 rounded-2xl border-2 border-leaf-100 p-3">
            <img src={p.src} alt={p.caption || "Event photo"} className="h-16 w-24 shrink-0 rounded-xl object-cover" loading="lazy" />
            <input
              data-testid={`events-caption-${i}`}
              className={captionCls}
              value={p.caption || ""}
              placeholder="Caption…"
              onChange={(e) => onCaption(i, e.target.value)}
            />
            <div className="flex shrink-0 items-center gap-1">
              <button
                data-testid={`events-move-up-${i}`}
                onClick={() => onMove(i, -1)}
                disabled={i === 0}
                aria-label="Move up"
                className="rounded-full p-2 text-leaf-700 hover:bg-leaf-50 disabled:opacity-30"
              >
                <ArrowUp className="h-4 w-4" />
              </button>
              <button
                data-testid={`events-move-down-${i}`}
                onClick={() => onMove(i, 1)}
                disabled={i === events.length - 1}
                aria-label="Move down"
                className="rounded-full p-2 text-leaf-700 hover:bg-leaf-50 disabled:opacity-30"
              >
                <ArrowDown className="h-4 w-4" />
              </button>
              <button
                data-testid={`events-remove-${i}`}
                onClick={() => onRemove(i)}
                aria-label="Remove from carousel"
                className="rounded-full p-2 text-clay-500 hover:bg-clay-500/10"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default function PhotosTab() {
  const { settings, refresh } = useSettings();
  const [photos, setPhotos] = useState([]);
  const [loadingLib, setLoadingLib] = useState(true);
  const [libErr, setLibErr] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState("");
  const [images, setImages] = useState({ ...IMAGES, ...(settings.images || {}) });
  const [events, setEvents] = useState(
    settings.events_photos?.length ? settings.events_photos : EVENTS_PHOTOS
  );
  const [picker, setPicker] = useState(null); // callback fn or null
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const fileRef = useRef(null);

  useEffect(() => {
    setImages({ ...IMAGES, ...(settings.images || {}) });
    setEvents(settings.events_photos?.length ? settings.events_photos : EVENTS_PHOTOS);
  }, [settings]);

  const loadLibrary = useCallback(async () => {
    setLoadingLib(true);
    try {
      const { data } = await axios.get(`${API}/photos`, { withCredentials: true });
      setPhotos(data || []);
      setLibErr("");
    } catch (e) {
      console.error("Failed to load photo library", e);
      setLibErr(e?.response?.data?.detail || "Failed to load the photo library");
    } finally {
      setLoadingLib(false);
    }
  }, []);

  useEffect(() => { loadLibrary(); }, [loadLibrary]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    setUploadErr("");
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        await axios.post(`${API}/photos/upload`, fd, { withCredentials: true });
      }
      await loadLibrary();
    } catch (err) {
      console.error("Photo upload failed", err);
      setUploadErr(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm("Remove this photo from the library?")) return;
    try {
      await axios.delete(`${API}/photos/${id}`, { withCredentials: true });
      setPhotos((prev) => prev.filter((p) => p.id !== id));
      setLibErr("");
    } catch (e) {
      console.error("Failed to delete photo", e);
      setLibErr("Couldn't delete photo — please refresh and try again.");
    }
  };

  const openSlotPicker = (slotKey) =>
    setPicker(() => (url) => {
      setImages((prev) => ({ ...prev, [slotKey]: url }));
      setPicker(null);
    });

  const openEventsPicker = () =>
    setPicker(() => (url) => {
      setEvents((prev) => [...prev, { src: url, caption: "" }]);
      setPicker(null);
    });

  const setCaption = (i, caption) =>
    setEvents((prev) => prev.map((x, j) => (j === i ? { ...x, caption } : x)));

  const moveEvent = (i, dir) => {
    setEvents((prev) => {
      const next = [...prev];
      const j = i + dir;
      if (j < 0 || j >= next.length) return prev;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const removeEvent = (i) => setEvents((prev) => prev.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    setSaveErr("");
    try {
      await axios.put(
        `${API}/settings`,
        { ...settings, images, events_photos: events },
        { withCredentials: true }
      );
      setSaved(true);
      await refresh();
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      console.error("Failed to save photo settings", e);
      setSaveErr(e?.response?.data?.detail || "Failed to save photos");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-photos" className="space-y-8">
      <LibrarySection
        photos={photos}
        loading={loadingLib}
        error={libErr}
        uploading={uploading}
        uploadErr={uploadErr}
        fileRef={fileRef}
        onUpload={handleUpload}
        onRefresh={loadLibrary}
        onDelete={deletePhoto}
      />
      <SlotsSection images={images} onChangeSlot={openSlotPicker} />
      <EventsSection
        events={events}
        onCaption={setCaption}
        onMove={moveEvent}
        onRemove={removeEvent}
        onAdd={openEventsPicker}
      />

      {/* Save bar */}
      <div className="sticky bottom-20 flex flex-wrap items-center gap-3 rounded-3xl border-2 border-leaf-200 bg-white/95 p-4 shadow-xl backdrop-blur md:bottom-4">
        {saveErr && (
          <span data-testid="photos-save-error" className="text-sm font-semibold text-clay-600">{saveErr}</span>
        )}
        {saved && (
          <span data-testid="photos-save-success" className="inline-flex items-center gap-1.5 text-sm font-semibold text-leaf-600">
            <CheckCircle2 className="h-4 w-4" /> Saved! Live on the site.
          </span>
        )}
        <button
          data-testid="photos-save"
          onClick={save}
          disabled={saving}
          className="ml-auto inline-flex items-center gap-2 rounded-full bg-leaf-600 px-6 py-2.5 text-sm font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-leaf-700 disabled:opacity-60"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? "Saving…" : "Save photos"}
        </button>
      </div>

      {picker && <PickerModal photos={photos} onPick={picker} onClose={() => setPicker(null)} />}
    </div>
  );
}
